/**
 * The full-size map's step A (DIRECTION §0.11; 196 (1), the owner: "multiple ways to win initially, then maybe it's
 * configurable per game how many win conditions there are"): ModeConfig.winConditions — which ways to win a points battle
 * count, and how many a side must meet. 'tickets': every other side out of tickets. 'hq': every other side's board HQ fallen
 * (core/hqFall.ts; never met off the board). 'majority': more than half the objectives held `majoritySeconds` on end, latched
 * once met (`PointsState.majorityMet`, stepped by core/capturePoints.ts stepCapture). 'nodes' (Oct 20, the owner's stand-in:
 * "% node captured"): `nodesShare` of all the objectives held as long, latched the same way. Pure.
 */
import type { GameState, PointsState, ModeConfig } from '../types';
import { WIN_CONDITIONS } from '../constants/action';
import { hqFallen } from './hqFall';
import { objectivesDone } from './storyObjectives';
import { territoryField } from './territory';
import { TERRITORY } from '../constants';

type WinConditions = NonNullable<ModeConfig['winConditions']>;

/** How many of the battle's win conditions `team` meets now (0 with none set). */
export function winConditionsMet(state: Pick<GameState, 'config' | 'modeState' | 'board' | 'structures' | 'players'>, team: number): number {
  const wc = state.config.modeConfig.winConditions;
  if (!wc) return 0;
  const others = [...new Set(state.players.map(p => p.teamIndex))].filter(t => t !== team);
  if (others.length === 0) return 0;
  const tickets = state.modeState.respawn?.tickets ?? {};
  let met = 0;
  for (const kind of wc.use) {
    if (kind === 'tickets' && state.modeState.respawn && others.every(t => (tickets[t] ?? 0) <= 0)) met++;
    else if (kind === 'hq' && others.every(t => hqFallen(state, t))) met++;
    else if (kind === 'majority' && state.modeState.points?.majorityMet?.includes(team)) met++;
    else if (kind === 'territory' && state.modeState.points?.territoryMet?.includes(team)) met++;
    else if (kind === 'nodes' && state.modeState.points?.nodesMet?.includes(team)) met++;
    else if (kind === 'objectives' && objectivesDone(state, team)) met++;   // 69.50: its story objectives all done
  }
  return met;
}

/** One step (`dt` seconds) of the 'majority' clock: who holds more than half the objectives, how long; latched at the mark. */
export function stepMajority(ps: PointsState, wc: WinConditions, dt: number): PointsState {
  const held = new Map<number, number>();
  for (const s of ps.sites) if (s.owner !== null) held.set(s.owner, (held.get(s.owner) ?? 0) + 1);
  let leader: number | null = null;
  for (const [t, n] of [...held].sort((a, b) => a[0] - b[0])) if (n * 2 > ps.sites.length) leader = t;
  if (leader === null) {
    if (!ps.majority) return ps;
    const { majority: _gone, ...rest } = ps;
    return rest;
  }
  const seconds = ps.majority?.team === leader ? ps.majority.seconds + dt : dt;
  const mark = wc.majoritySeconds ?? WIN_CONDITIONS.majoritySeconds;
  const met = seconds >= mark && !(ps.majorityMet ?? []).includes(leader) ? [...(ps.majorityMet ?? []), leader] : ps.majorityMet;
  return { ...ps, majority: { team: leader, seconds }, ...(met ? { majorityMet: met } : {}) };
}

/** 216 (4): one step (`dt` seconds) of the 'territory' clock — the side holding TERRITORY.winShare of the planet, how long; latched at TERRITORY.holdSeconds. */
export function stepTerritory(state: GameState, ps: PointsState, dt: number): PointsState {
  const field = territoryField(state);
  let leader: number | null = null;
  if (field) for (const [t, share] of Object.entries(field.share).sort((a, b) => Number(a[0]) - Number(b[0]))) if (share >= TERRITORY.winShare) leader = Number(t);
  if (leader === null) {
    if (!ps.territory) return ps;
    const { territory: _gone, ...rest } = ps;
    return rest;
  }
  const seconds = ps.territory?.team === leader ? ps.territory.seconds + dt : dt;
  const met = seconds >= TERRITORY.holdSeconds && !(ps.territoryMet ?? []).includes(leader) ? [...(ps.territoryMet ?? []), leader] : ps.territoryMet;
  return { ...ps, territory: { team: leader, seconds }, ...(met ? { territoryMet: met } : {}) };
}

/** Oct 20 (the owner's stand-in, "% node captured"): one step (`dt` seconds) of the 'nodes' clock — the side holding `nodesShare` of all the objectives, how long; latched at `majoritySeconds`. */
export function stepNodes(ps: PointsState, wc: WinConditions, dt: number): PointsState {
  const held = new Map<number, number>();
  for (const s of ps.sites) if (s.owner !== null) held.set(s.owner, (held.get(s.owner) ?? 0) + 1);
  const need = (wc.nodesShare ?? WIN_CONDITIONS.nodesShare) * ps.sites.length;
  let leader: number | null = null;
  if (ps.sites.length > 0) for (const [t, n] of [...held].sort((a, b) => a[0] - b[0])) if (n >= need && (leader === null || n > held.get(leader)!)) leader = t;
  if (leader === null) {
    if (!ps.nodes) return ps;
    const { nodes: _gone, ...rest } = ps;
    return rest;
  }
  const seconds = ps.nodes?.team === leader ? ps.nodes.seconds + dt : dt;
  const mark = wc.majoritySeconds ?? WIN_CONDITIONS.majoritySeconds;
  const met = seconds >= mark && !(ps.nodesMet ?? []).includes(leader) ? [...(ps.nodesMet ?? []), leader] : ps.nodesMet;
  return { ...ps, nodes: { team: leader, seconds }, ...(met ? { nodesMet: met } : {}) };
}
